// ============================================================================
// MÓDULO 7 - UNIDAD 6: HOME & DAILY ROUTINES
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Collocations (make/do/clean/wash) + Tense Contrast
// Vocabulario: House types, rooms, furniture, appliances
// UK-US variations: flat/apartment, garden/yard, etc.
// ============================================================================

export const M7_UNIT_6 = {
  id: "m7-u6",
  title: "Unit 6: Home & Daily Routines",
  topic: "Master house vocabulary, household collocations, UK-US differences, and contrast routine vs current actions perfectly!",
  
  grammarImage: "/images/grammar/m7-u6-collocations.jpg",
  grammarText: `
    <h3>Home & Daily Routines - Collocations & Tense Contrast</h3>
    
    <div class="grammar-rule">
      <h4>Household Collocations (Fixed Expressions)</h4>
      <p>Certain verbs ALWAYS go with certain household tasks:</p>
      
      <div class="collocation-group">
        <h5>MAKE (crear algo):</h5>
        <ul>
          <li>✅ Make the bed</li>
          <li>✅ Make breakfast/lunch/dinner</li>
          <li>✅ Make a mess</li>
          <li>❌ NOT: "do the bed"</li>
        </ul>
      </div>
      
      <div class="collocation-group">
        <h5>DO (realizar tareas):</h5>
        <ul>
          <li>✅ Do the laundry</li>
          <li>✅ Do the dishes</li>
          <li>✅ Do the cleaning</li>
          <li>✅ Do the ironing</li>
          <li>❌ NOT: "make the laundry"</li>
        </ul>
      </div>
      
      <div class="collocation-group">
        <h5>OTHER COMMON VERBS:</h5>
        <ul>
          <li>✅ Clean the windows</li>
          <li>✅ Wash the dishes / Wash up (UK)</li>
          <li>✅ Tidy the room / Tidy up</li>
          <li>✅ Vacuum the carpet</li>
          <li>✅ Clear the table</li>
          <li>✅ Take out the rubbish/trash</li>
        </ul>
      </div>
    </div>

    <div class="tense-contrast">
      <h4>Tense Contrast: Routine vs. Right Now</h4>
      
      <p><strong>Present Simple (Routines/Habits):</strong></p>
      <ul>
        <li>I usually make the bed in the morning</li>
        <li>She does the laundry on Sundays</li>
        <li>We always cook at home</li>
      </ul>
      
      <p><strong>Present Continuous (Current Actions):</strong></p>
      <ul>
        <li>I'm making the bed right now</li>
        <li>She's doing the laundry at the moment</li>
        <li>We're cooking dinner tonight</li>
      </ul>
      
      <p><strong>Contrast in Same Sentence:</strong></p>
      <ul>
        <li>I usually cook at home, but I'm eating out tonight</li>
        <li>She normally tidies her room, but she's leaving it messy today</li>
      </ul>
    </div>

    <div class="uk-us-differences">
      <h4>UK-US Vocabulary Differences</h4>
      <table>
        <tr><th>UK</th><th>US</th><th>Spanish</th></tr>
        <tr><td>Flat</td><td>Apartment</td><td>Apartamento</td></tr>
        <tr><td>Garden</td><td>Yard</td><td>Jardín</td></tr>
        <tr><td>Tap</td><td>Faucet</td><td>Grifo</td></tr>
        <tr><td>Rubbish</td><td>Trash</td><td>Basura</td></tr>
        <tr><td>Wardrobe</td><td>Closet</td><td>Armario</td></tr>
      </table>
    </div>
  `,

  iconType: "home",

  sets: [
    // ========================================================================
    // SET 1: WELCOME TO YOUR NEW HOME 🏠 - HOUSE TYPES
    // ========================================================================
    {
      id: "m7-u6-s1",
      title: "Set 1: Welcome to Your New Home 🏠",
      context: `**At the Estate Agent's Office**

Agent: "Good morning! I have several properties to show you today. What type of home are you looking for?"

Customer: "We're open to options. Can you explain the different types?"`,
      
      questions: [
        {
          type: "match",
          q: "Match house types with their descriptions:",
          pairs: [
            { left: "Detached house", right: "Standalone home, not connected to others" },
            { left: "Semi-detached", right: "House sharing one wall with neighbor" },
            { left: "Terraced house", right: "Row of connected houses" },
            { left: "Flat/Apartment", right: "Unit within a larger building" }
          ],
          successtip: "✓ HOUSE TYPES!\nUK: 'Terraced house' = US: 'Row house' or 'Townhouse'. Same concept, different name! 🏘️🇬🇧🇺🇸"
        },
        {
          type: "mc",
          q: "Agent: 'This is a lovely ___ house - completely independent with space all around.'",
          options: [
            "detached",
            "semi-detached",
            "terraced"
          ],
          a: "detached",
          successtip: "✓ MOST PRIVATE!\n'Detached' = most expensive usually, maximum privacy, no shared walls! 🏡✨"
        },
        {
          type: "blank",
          q: "In the UK, an apartment is called a ___.",
          a: "flat",
          accept: { ignoreCase: true },
          successtip: "✓ UK TERM!\n'Flat' (UK) = 'Apartment' (US). Same thing! British English uses 'flat' more commonly! 🇬🇧🏢"
        },
        {
          type: "mc",
          q: "Agent: 'If you want something affordable, a ___ house shares one wall with the neighbor.'",
          options: [
            "semi-detached",
            "detached",
            "bungalow"
          ],
          a: "semi-detached",
          successtip: "✓ HALF ATTACHED!\n'Semi-detached' = middle ground between detached and terraced. Common in UK suburbs! 🏠🏠"
        },
        {
          type: "order",
          q: "Traduce: Estamos buscando una casa adosada en el centro de la ciudad",
          a: "We are looking for a terraced house in the city center",
          successtip: "✓ URBAN LIVING!\nTerraced houses = very common in UK cities. Efficient use of space! 🏙️🏘️"
        },
        {
          type: "mc",
          q: "Agent: 'A ___ is a single-story house - no stairs!'",
          options: [
            "bungalow",
            "cottage",
            "mansion"
          ],
          a: "bungalow",
          successtip: "✓ ONE LEVEL!\nBungalows = popular with elderly people or those with mobility issues. All on ground floor! 🏡♿"
        },
        {
          type: "blank",
          q: "A small house in the countryside is often called a ___.",
          a: "cottage",
          accept: { ignoreCase: true },
          successtip: "✓ RURAL CHARM!\n'Cottage' = usually small, traditional, countryside home. Very British! Often thatched roof! 🏡🌾🇬🇧"
        },
        {
          type: "mc",
          q: "In American English, what's the common term for 'flat'?",
          options: [
            "Apartment",
            "Condo",
            "Unit"
          ],
          a: "Apartment",
          successtip: "✓ US TERM!\n'Apartment' (US) = 'Flat' (UK). 'Condo' = owned apartment. All similar concepts! 🇺🇸🏢"
        },
        {
          type: "order",
          q: "Traduce: ¿Cuántos dormitorios tiene este apartamento?",
          a: "How many bedrooms does this flat have?",
          successtip: "✓ BEDROOM COUNT!\nUK: 'flat', US: 'apartment' - both correct! Bedrooms = key selling point! 🛏️🔑"
        },
        {
          type: "mc",
          q: "Agent: 'This property is a beautiful ___ - perfect for a family, with three floors!'",
          options: [
            "terraced house",
            "bungalow",
            "studio flat"
          ],
          a: "terraced house",
          successtip: "✓ FAMILY HOME!\nTerraced houses often have multiple floors: ground, first, second. More vertical space! 🏠📏"
        }
      ]
    },

    // ========================================================================
    // SET 2: ROOM BY ROOM TOUR 🚪 - ALL ROOMS
    // ========================================================================
    {
      id: "m7-u6-s2",
      title: "Set 2: Room by Room Tour 🚪",
      context: `**First Day in New Flat - Roommate Tour**

Roommate: "Welcome! Let me show you around. We have quite a few rooms here..."

New Tenant: "Great! I'd love to see everything."`,
      
      questions: [
        {
          type: "match",
          q: "Match rooms with their primary purposes:",
          pairs: [
            { left: "Living room", right: "Relax, watch TV, entertain guests" },
            { left: "Kitchen", right: "Prepare and cook food" },
            { left: "Bedroom", right: "Sleep and rest" },
            { left: "Bathroom", right: "Wash, shower, use toilet" }
          ],
          successtip: "✓ BASIC ROOMS!\nEvery home needs these four! Everything else is bonus! 🏠✨"
        },
        {
          type: "mc",
          q: "Roommate: 'This is the ___ - we call it the sitting room. We relax and watch TV here.'",
          options: [
            "living room",
            "bedroom",
            "kitchen"
          ],
          a: "living room",
          successtip: "✓ MANY NAMES!\nUK: 'sitting room', 'lounge', or 'living room'. US: usually 'living room'. All mean same! 🛋️📺"
        },
        {
          type: "blank",
          q: "In British English, the room with the toilet is sometimes called the ___ (not bathroom).",
          a: "toilet",
          accept: { ignoreCase: true, alts: ["loo", "WC"] },
          successtip: "✓ UK DISTINCTION!\nUK: 'Toilet' = just WC. 'Bathroom' = has bath/shower. US: 'bathroom' = both! 🚽🛁"
        },
        {
          type: "mc",
          q: "Roommate: 'Here's the ___ - where we eat our meals together.'",
          options: [
            "dining room",
            "kitchen",
            "study"
          ],
          a: "dining room",
          successtip: "✓ FORMAL EATING!\nDining room = separate eating space. Not all homes have one - many eat in kitchen! 🍽️"
        },
        {
          type: "order",
          q: "Traduce: Tenemos tres dormitorios y dos baños",
          a: "We have three bedrooms and two bathrooms",
          successtip: "✓ KEY SPECS!\n'3 bed, 2 bath' = standard real estate shorthand. Important property details! 🛏️🚿"
        },
        {
          type: "mc",
          q: "Roommate: 'This small room is my ___ - where I work from home.'",
          options: [
            "study / home office",
            "bedroom",
            "living room"
          ],
          a: "study / home office",
          successtip: "✓ WORK SPACE!\n'Study' (UK) or 'home office' (US/UK) = dedicated work room. Essential post-pandemic! 💻🏠"
        },
        {
          type: "blank",
          q: "The passage between rooms is called a ___ or corridor.",
          a: "hallway",
          accept: { ignoreCase: true, alts: ["corridor", "hall"] },
          successtip: "✓ CONNECTING SPACE!\n'Hallway' (US) = 'Hall' or 'Corridor' (UK). The walkway between rooms! 🚪🚶"
        },
        {
          type: "mc",
          q: "Roommate: 'The ___ room has our washing machine and dryer.'",
          options: [
            "utility",
            "living",
            "dining"
          ],
          a: "utility",
          successtip: "✓ LAUNDRY ROOM!\n'Utility room' (UK) = laundry room + storage. Very British! US: often in basement/garage! 🧺🇬🇧"
        },
        {
          type: "order",
          q: "Traduce: El ático está en el último piso, debajo del techo",
          a: "The attic is on the top floor, below the roof",
          successtip: "✓ TOP SPACE!\n'Attic' (US) = 'Loft' (UK). Often storage, sometimes converted to bedroom! 📦🏠"
        },
        {
          type: "mc",
          q: "Roommate: 'And finally, the ___ - it's below ground level, great for storage!'",
          options: [
            "basement",
            "attic",
            "garage"
          ],
          a: "basement",
          successtip: "✓ UNDERGROUND!\n'Basement' = below ground. UK: 'cellar'. Common in US, rarer in UK! 🏠⬇️"
        }
      ]
    },

    // ========================================================================
    // SET 3: OUTDOOR SPACES 🌳 - GARDEN/YARD
    // ========================================================================
    {
      id: "m7-u6-s3",
      title: "Set 3: Outdoor Spaces 🌳",
      context: `**Weekend Gardening**

Neighbor: "Your outdoor space looks great! Are you planning to do any gardening this weekend?"

You: "Yes! I'm learning all the terminology..."`,
      
      questions: [
        {
          type: "mc",
          q: "The outdoor space behind/around a house is called a ___ in the UK.",
          options: [
            "garden",
            "yard",
            "lawn"
          ],
          a: "garden",
          successtip: "✓ UK vs US!\nUK: 'garden' (any outdoor space). US: 'yard'. 'Garden' (US) = just flower/veggie area! 🌻🇬🇧🇺🇸"
        },
        {
          type: "blank",
          q: "In American English, the outdoor area is usually called a ___.",
          a: "yard",
          accept: { ignoreCase: true },
          successtip: "✓ US TERM!\n'Yard' (US) = 'garden' (UK). Americans have 'front yard' and 'back yard'! 🏡🇺🇸"
        },
        {
          type: "mc",
          q: "Neighbor: 'Your ___ needs cutting - the grass is quite long!'",
          options: [
            "lawn",
            "driveway",
            "patio"
          ],
          a: "lawn",
          successtip: "✓ GRASS AREA!\n'Lawn' = the grass part of your garden/yard. Needs regular mowing! 🌱✂️"
        },
        {
          type: "order",
          q: "Traduce: Necesito cortar el césped con la cortadora de césped",
          a: "I need to cut the lawn with the lawn mower",
          successtip: "✓ GARDEN TOOL!\n'Lawn mower' = máquina cortacésped. Also: 'mow the lawn' = verb! Electric or petrol! 🚜🌱"
        },
        {
          type: "mc",
          q: "The paved area outside where you park your car is the ___.",
          options: [
            "driveway",
            "garage",
            "patio"
          ],
          a: "driveway",
          successtip: "✓ PARKING SPACE!\n'Driveway' = path from street to garage/house. Private parking! 🚗🏠"
        },
        {
          type: "blank",
          q: "A small building in the garden for storing tools is a ___.",
          a: "shed",
          accept: { ignoreCase: true },
          successtip: "✓ STORAGE!\n'Shed' = garden storage. Very British! Often wooden. 'Man cave' jokes common! 🛠️🏚️😄"
        },
        {
          type: "mc",
          q: "Neighbor: 'You have a nice ___ - perfect for barbecues!'",
          options: [
            "patio",
            "lawn",
            "shed"
          ],
          a: "patio",
          successtip: "✓ OUTDOOR ENTERTAINING!\n'Patio' = paved outdoor area. US also: 'deck' (wooden platform). BBQ central! 🍖☀️"
        },
        {
          type: "mc",
          q: "A ___ is where you keep your car protected from weather.",
          options: [
            "garage",
            "driveway",
            "shed"
          ],
          a: "garage",
          successtip: "✓ CAR SHELTER!\n'Garage' = car storage/protection. Often becomes storage for everything BUT the car! 😄📦🚗"
        },
        {
          type: "order",
          q: "Traduce: Vamos a instalar una cerca alrededor del jardín",
          a: "We are going to install a fence around the garden",
          successtip: "✓ BOUNDARY!\n'Fence' = barrier around property. Important for privacy and security! 🏡🔒"
        },
        {
          type: "mc",
          q: "In the UK, the paved path along the street is called the ___.",
          options: [
            "pavement",
            "sidewalk",
            "driveway"
          ],
          a: "pavement",
          successtip: "✓ WALKING PATH!\nUK: 'pavement' = US: 'sidewalk'. Confusing: US 'pavement' = UK 'road surface'! 🚶🇬🇧🇺🇸"
        }
      ]
    },

    // ========================================================================
    // SET 4: FURNISHING EACH ROOM 🛋️ - FURNITURE
    // ========================================================================
    {
      id: "m7-u6-s4",
      title: "Set 4: Furnishing Each Room 🛋️",
      context: `**Shopping for Furniture**

Sales Assistant: "Welcome! What room are you furnishing today?"

Customer: "Actually, multiple rooms. I just moved in!"`,
      
      questions: [
        {
          type: "match",
          q: "Match furniture items to their typical rooms:",
          pairs: [
            { left: "Sofa", right: "Living room" },
            { left: "Wardrobe", right: "Bedroom" },
            { left: "Dining table", right: "Dining room/Kitchen" },
            { left: "Bookshelf", right: "Living room/Study" }
          ],
          successtip: "✓ ROOM FURNITURE!\nSome furniture flexible (bookshelf anywhere), others room-specific (toilet)! 🏠🛋️"
        },
        {
          type: "mc",
          q: "Sales Assistant: 'For the living room, you'll need a ___ - that's the main seating.'",
          options: [
            "sofa",
            "bed",
            "table"
          ],
          a: "sofa",
          successtip: "✓ KEY SEATING!\n'Sofa' (UK/US) = 'Couch' (US). Same thing! Center of living room! 🛋️📺"
        },
        {
          type: "blank",
          q: "A comfortable single chair in the living room is an ___.",
          a: "armchair",
          accept: { ignoreCase: true },
          successtip: "✓ COMFY SEAT!\n'Armchair' = chair with arms (obviously!). Perfect for reading! 📖🪑"
        },
        {
          type: "mc",
          q: "Sales Assistant: 'This low table goes in front of the sofa - it's a ___.'",
          options: [
            "coffee table",
            "dining table",
            "desk"
          ],
          a: "coffee table",
          successtip: "✓ CENTER TABLE!\n'Coffee table' = low table for drinks, magazines, feet (secretly!). Living room essential! ☕📰"
        },
        {
          type: "order",
          q: "Traduce: Necesitamos una alfombra grande para la sala de estar",
          a: "We need a large rug for the living room",
          successtip: "✓ FLOOR COVERING!\n'Rug' = small carpet, moveable. 'Carpet' = covers whole floor, fixed. Both add warmth! 🏠🟫"
        },
        {
          type: "mc",
          q: "For the bedroom, where you hang clothes, you need a ___ (UK term).",
          options: [
            "wardrobe",
            "closet",
            "cupboard"
          ],
          a: "wardrobe",
          successtip: "✓ UK FURNITURE!\nUK: 'wardrobe' (furniture piece). US: 'closet' (built-in). UK 'closet' = small room! 🇬🇧👔"
        },
        {
          type: "blank",
          q: "American English uses ___ for the built-in clothes storage (UK: wardrobe).",
          a: "closet",
          accept: { ignoreCase: true },
          successtip: "✓ US BUILT-IN!\n'Closet' (US) = built into wall. 'Wardrobe' (UK) = furniture piece. Same function! 🇺🇸👗"
        },
        {
          type: "mc",
          q: "Sales Assistant: 'This furniture has drawers for clothes - it's a ___.' (UK term)",
          options: [
            "chest of drawers",
            "dresser",
            "wardrobe"
          ],
          a: "chest of drawers",
          successtip: "✓ UK TERM!\nUK: 'chest of drawers'. US: 'dresser'. Multiple drawers for folded clothes! 🇬🇧👕📦"
        },
        {
          type: "order",
          q: "Traduce: La mesita de noche está al lado de la cama",
          a: "The bedside table is next to the bed",
          successtip: "✓ BEDROOM ESSENTIAL!\nUK: 'bedside table'. US: 'nightstand'. For lamp, alarm, phone! 🛏️💡📱"
        },
        {
          type: "mc",
          q: "Complete: 'I keep my books on a ___ in the study.'",
          options: [
            "bookshelf",
            "wardrobe",
            "drawer"
          ],
          a: "bookshelf",
          successtip: "✓ BOOK STORAGE!\n'Bookshelf' or 'bookcase' (bigger). Essential for readers! UK: sometimes 'bookshelves' (plural)! 📚🏠"
        }
      ]
    },

    // ========================================================================
    // SET 5: KITCHEN & BATHROOM ESSENTIALS 🍳🛁
    // ========================================================================
    {
      id: "m7-u6-s5",
      title: "Set 5: Kitchen & Bathroom Essentials 🍳",
      context: `**Moving In - Unpacking Kitchen & Bathroom**

Friend: "Wow, you have a lot of stuff! Let me help you put things in the right places..."`,
      
      questions: [
        {
          type: "match",
          q: "Match kitchen appliances with their uses:",
          pairs: [
            { left: "Dishwasher", right: "Washes dishes automatically" },
            { left: "Kettle", right: "Boils water for tea/coffee" },
            { left: "Fridge", right: "Keeps food cold" },
            { left: "Cooker", right: "For cooking food (stove)" }
          ],
          successtip: "✓ KITCHEN ESSENTIALS!\nKettle = VERY British! Used multiple times daily for tea! ☕🇬🇧"
        },
        {
          type: "mc",
          q: "Friend: 'The ___ keeps food cold. Let's plug it in first!'",
          options: [
            "fridge",
            "cooker",
            "dishwasher"
          ],
          a: "fridge",
          successtip: "✓ REFRIGERATION!\n'Fridge' = short for 'refrigerator'. 'Freezer' = for frozen food (often attached)! 🧊🥶"
        },
        {
          type: "blank",
          q: "In the UK, the appliance for cooking is called a ___ (US: stove).",
          a: "cooker",
          accept: { ignoreCase: true, alts: ["oven"] },
          successtip: "✓ UK COOKING!\nUK: 'cooker' (stove + oven together). US: 'stove'. 'Oven' = the box part! 🇬🇧🍳"
        },
        {
          type: "mc",
          q: "Friend: 'Where do you want the ___? It washes dishes automatically.'",
          options: [
            "dishwasher",
            "washing machine",
            "kettle"
          ],
          a: "dishwasher",
          successtip: "✓ DISH CLEANER!\n'Dishwasher' = dish cleaning machine. Different from 'washing machine' (clothes)! 🍽️🤖"
        },
        {
          type: "order",
          q: "Traduce: Necesito una cacerola para cocinar la sopa",
          a: "I need a saucepan to cook the soup",
          successtip: "✓ COOKING POT!\n'Saucepan' = pot with handle. Different from 'frying pan' (flat, for frying)! 🍲🥘"
        },
        {
          type: "mc",
          q: "Friend: 'The ___ is essential in the UK - for making tea!'",
          options: [
            "kettle",
            "teapot",
            "coffee maker"
          ],
          a: "kettle",
          successtip: "✓ BRITISH ESSENTIAL!\nKettle = boils water. Teapot = brews tea. Brits use kettle 5+ times daily! ☕🇬🇧💙"
        },
        {
          type: "blank",
          q: "In the bathroom, the sink is called a ___ in British English.",
          a: "basin",
          accept: { ignoreCase: true, alts: ["wash basin", "washbasin"] },
          successtip: "✓ UK BATHROOM!\nUK: 'basin' or 'wash basin'. US: just 'sink'. Where you wash hands/face! 🇬🇧🚰"
        },
        {
          type: "mc",
          q: "Friend: 'The ___ is where you wash your hands. Americans call it a sink.'",
          options: [
            "basin",
            "bath",
            "shower"
          ],
          a: "basin",
          successtip: "✓ HAND WASHING!\n'Basin' (UK) = 'Sink' (US). Kitchen also has sink/basin! 🚰🧼"
        },
        {
          type: "mc",
          q: "The UK word for the water control is ___ (US: faucet).",
          options: [
            "tap",
            "knob",
            "handle"
          ],
          a: "tap",
          successtip: "✓ WATER CONTROL!\nUK: 'tap'. US: 'faucet'. Turn it to get water! Often separate hot/cold in UK! 🚰🇬🇧"
        },
        {
          type: "order",
          q: "Traduce: La bañera está en el baño principal",
          a: "The bathtub is in the main bathroom",
          successtip: "✓ TUB!\n'Bathtub' (US) or just 'bath' (UK). For soaking! Different from shower! 🛁💧"
        }
      ]
    },

    // ========================================================================
    // SET 6: MORNING CHORES MATCH 🧹 - COLLOCATIONS
    // ========================================================================
    {
      id: "m7-u6-s6",
      title: "Set 6: Household Collocations - MAKE vs DO 🧹",
      context: `**Saturday Morning - Chore Day**

Parent: "Okay kids, time for Saturday chores! Remember, we use specific verbs for each task..."`,
      
      questions: [
        {
          type: "match",
          q: "Match verbs with their correct household collocations:",
          pairs: [
            { left: "MAKE", right: "the bed, breakfast, dinner" },
            { left: "DO", right: "the laundry, the dishes, the cleaning" },
            { left: "CLEAN", right: "the windows, the floor" },
            { left: "WASH", right: "the car, the dishes, up (UK)" }
          ],
          successtip: "✓ COLLOCATION RULES!\nThese are FIXED! You can't change the verb! 'Do the bed' = WRONG! ❌🛏️"
        },
        {
          type: "mc",
          q: "Parent: 'First, everyone needs to ___ their beds.'",
          options: [
            "make",
            "do",
            "fix"
          ],
          a: "make",
          tip: "🎯 KEY COLLOCATION!\n\n✅ MAKE the bed (CORRECT)\n❌ DO the bed (WRONG)\n❌ FIX the bed (WRONG)\n\nWhy 'make'?\n'Make' = create/arrange something\nYou're creating an orderly bed!\n\nThis is one of the most important collocations!\nYou MUST use 'make' with 'bed'! 🛏️",
          successtip: "✓ MAKE THE BED!\nMost common morning chore! 'Making the bed' = arranging covers neatly! 🛏️✨"
        },
        {
          type: "blank",
          q: "Parent: 'I'll ___ the laundry today.' (verb)",
          a: "do",
          accept: { ignoreCase: true },
          successtip: "✓ DO THE LAUNDRY!\n'Do the laundry' (US) = 'do the washing' (UK). Never 'make the laundry'! 🧺👕"
        },
        {
          type: "mc",
          q: "Parent: 'After breakfast, someone needs to ___ the dishes.'",
          options: [
            "do / wash",
            "make",
            "clean"
          ],
          a: "do / wash",
          tip: "🎯 TWO CORRECT VERBS!\n\n✅ DO the dishes (common)\n✅ WASH the dishes (also correct)\n❌ MAKE the dishes (WRONG!)\n\nBoth work!\n• 'Do the dishes' = more general\n• 'Wash the dishes' = more specific\n\nUK also: 'do the washing up' or 'wash up'\n\nBut NEVER 'make' with dishes! 🍽️",
          successtip: "✓ BOTH WORK!\n'Do the dishes' OR 'wash the dishes' = both correct! UK: 'washing up'! 🍽️💧"
        },
        {
          type: "order",
          q: "Traduce: Necesito hacer la cama antes del desayuno",
          a: "I need to make the bed before breakfast",
          successtip: "✓ MAKE + BED!\nRemember: MAKE the bed (not 'do'). This is a fixed collocation! 🛏️✅"
        },
        {
          type: "mc",
          q: "Parent: 'I'm going to ___ breakfast for everyone.'",
          options: [
            "make",
            "do",
            "cook"
          ],
          a: "make",
          successtip: "✓ MAKE MEALS!\n'Make breakfast/lunch/dinner' = prepare food. 'Cook' also works but 'make' more common! 🍳🥐"
        },
        {
          type: "blank",
          q: "Parent: 'Who wants to ___ the cleaning this week?' (verb)",
          a: "do",
          accept: { ignoreCase: true },
          successtip: "✓ DO CLEANING!\n'Do the cleaning' = general house cleaning. Not 'make the cleaning'! 🧹🧽"
        },
        {
          type: "mc",
          q: "Which is CORRECT?",
          options: [
            "Do the ironing",
            "Make the ironing",
            "Clean the ironing"
          ],
          a: "Do the ironing",
          successtip: "✓ DO + IRONING!\n'Do the ironing' = iron clothes. Another 'do' collocation! Never 'make'! 👔🔥"
        },
        {
          type: "order",
          q: "Traduce: Él está haciendo la colada ahora mismo",
          a: "He is doing the laundry right now",
          successtip: "✓ PRESENT CONTINUOUS!\n'Is doing the laundry' = happening NOW. Notice: DO (not make) the laundry! 🧺⏰"
        },
        {
          type: "mc",
          q: "Summary: We use 'MAKE' with ___ but 'DO' with ___.",
          options: [
            "the bed, breakfast / the laundry, the dishes",
            "the laundry / the bed",
            "everything / nothing"
          ],
          a: "the bed, breakfast / the laundry, the dishes",
          successtip: "✓ PATTERN!\nMAKE = creating things (bed, food). DO = completing tasks (laundry, dishes)! 🎯"
        }
      ]
    },

    // ========================================================================
    // SET 7: MORE HOUSEHOLD COLLOCATIONS 🧽
    // ========================================================================
    {
      id: "m7-u6-s7",
      title: "Set 7: More Household Verbs 🧽",
      context: `**Preparing for Guests**

You: "My parents are visiting tomorrow! I need to clean everything!"

Friend: "I'll help! Let's divide the tasks..."`,
      
      questions: [
        {
          type: "mc",
          q: "Friend: 'I'll ___ the windows - they're quite dirty.'",
          options: [
            "clean",
            "do",
            "make"
          ],
          a: "clean",
          successtip: "✓ CLEAN + WINDOWS!\n'Clean the windows' = wash/wipe windows. Spring cleaning classic! 🪟✨"
        },
        {
          type: "blank",
          q: "You: 'Can you ___ the floor too? It needs sweeping.' (verb)",
          a: "clean",
          accept: { ignoreCase: true, alts: ["sweep", "mop"] },
          successtip: "✓ FLOOR CLEANING!\n'Clean/sweep/mop the floor' = all work! Different methods, same goal! 🧹🧽"
        },
        {
          type: "mc",
          q: "Friend: 'Should I ___ the carpet? You have a vacuum cleaner, right?'",
          options: [
            "vacuum",
            "clean",
            "wash"
          ],
          a: "vacuum",
          tip: "🎯 CARPET COLLOCATION!\n\n✅ VACUUM the carpet (most specific)\n✅ CLEAN the carpet (general, also works)\n❌ WASH the carpet (not normal!)\n\n'Vacuum' = use vacuum cleaner\nVery specific verb for carpets!\n\nUK sometimes: 'hoover the carpet'\n(Hoover = brand name, became verb!)\n\nBest answer: vacuum! 🧹",
          successtip: "✓ VACUUM!\n'Vacuum the carpet' = use vacuum cleaner. UK slang: 'hoover' (brand became verb)! 🧹🇬🇧"
        },
        {
          type: "order",
          q: "Traduce: Necesito ordenar mi habitación antes de que lleguen",
          a: "I need to tidy my room before they arrive",
          successtip: "✓ TIDY UP!\n'Tidy the room' = organize, put things away. Very British verb! 🇬🇧🧹"
        },
        {
          type: "mc",
          q: "You: 'After dinner, we need to ___ the table.'",
          options: [
            "clear",
            "clean",
            "empty"
          ],
          a: "clear",
          tip: "🎯 TABLE COLLOCATION!\n\n✅ CLEAR the table (remove items after eating)\n✅ CLEAN the table (wipe it)\n✅ SET the table (put plates/cutlery)\n\nDifferent meanings!\n\n'Clear' = take dishes away\n'Clean' = wipe surface\n'Set' = prepare for eating\n\nAfter dinner = CLEAR! 🍽️",
          successtip: "✓ AFTER MEAL!\n'Clear the table' = remove dishes after eating. Then 'clean' it (wipe)! Two steps! 🍽️"
        },
        {
          type: "blank",
          q: "In UK English, washing dishes is called 'doing the ___ up' or just '___ up'.",
          a: "washing",
          accept: { ignoreCase: true, alts: ["washing / wash", "wash"] },
          successtip: "✓ BRITISH PHRASE!\n'Do the washing up' or 'wash up' (UK) = do the dishes (US). Very British! 🇬🇧🍽️"
        },
        {
          type: "mc",
          q: "Friend: 'I'll ___ out the rubbish before your parents arrive.'",
          options: [
            "take",
            "make",
            "do"
          ],
          a: "take",
          successtip: "✓ TRASH REMOVAL!\n'Take out the rubbish' (UK) / 'trash' (US) = bring garbage outside! 🗑️🚪"
        },
        {
          type: "mc",
          q: "UK: 'Rubbish' = US: '___'",
          options: [
            "Trash / Garbage",
            "Waste",
            "Dirt"
          ],
          a: "Trash / Garbage",
          successtip: "✓ TRASH TALK!\nUK: 'rubbish'. US: 'trash' or 'garbage'. UK: 'bin'. US: 'trash can'. Same stuff! 🗑️🇬🇧🇺🇸"
        },
        {
          type: "order",
          q: "Traduce: Voy a limpiar el baño y aspirar las alfombras",
          a: "I am going to clean the bathroom and vacuum the carpets",
          successtip: "✓ DOUBLE TASK!\n'Clean' + 'vacuum' = two different verbs for two different tasks! 🚽🧹"
        },
        {
          type: "mc",
          q: "Complete: 'The living room looks messy. Let's ___ it ___.'",
          options: [
            "tidy / up",
            "make / up",
            "do / up"
          ],
          a: "tidy / up",
          successtip: "✓ PHRASAL VERB!\n'Tidy up' = organize, make neat. 'Tidy' alone or 'tidy up' both work! 🧹✨"
        }
      ]
    },

    // ========================================================================
    // SET 8: ROUTINE VS RIGHT NOW ⏰ - TENSE CONTRAST
    // ========================================================================
    {
      id: "m7-u6-s8",
      title: "Set 8: Routine vs Right Now ⏰",
      context: `**Phone Call During Chores**

Friend calls: "Hey! Want to grab coffee?"

You: "I can't right now, I'm busy with housework..."`,
      
      questions: [
        {
          type: "mc",
          q: "You: 'I usually ___ the bed every morning, but today I forgot!'",
          options: [
            "make",
            "am making",
            "made"
          ],
          a: "make",
          tip: "⏰ ROUTINE vs NOW!\n\n'Usually' = routine = Present Simple!\n✅ 'I usually make' (habit)\n❌ 'I usually am making' (WRONG!)\n\nPresent Simple = routines, habits, general truths\nPresent Continuous = actions happening NOW\n\n'Usually', 'always', 'normally' = signals for Simple\n'Right now', 'at the moment' = signals for Continuous\n\nKey Unit 6 concept! 🎯",
          successtip: "✓ HABIT!\n'Usually' = present simple for routine. 'Am making' = right now action! ⏰"
        },
        {
          type: "blank",
          q: "Friend: 'What are you doing?' You: 'I ___ the dishes right now.' (wash + -ing)",
          a: "am washing",
          accept: { ignoreCase: true, alts: ["'m washing"] },
          successtip: "✓ RIGHT NOW!\n'Right now' = present continuous! 'Am washing' = happening at this moment! 🍽️⏰"
        },
        {
          type: "mc",
          q: "You: 'I normally ___ the laundry on Sundays.' (routine)",
          options: [
            "do",
            "am doing",
            "did"
          ],
          a: "do",
          successtip: "✓ WEEKLY ROUTINE!\n'Normally' + Sunday = routine = present simple 'do'! 🧺📅"
        },
        {
          type: "order",
          q: "Traduce: Generalmente cocino en casa, pero esta noche voy a salir",
          a: "I usually cook at home, but tonight I am going out",
          successtip: "✓ CONTRAST!\nPresent simple (usually cook) vs going out (tonight exception) = natural contrast! 🍳🍽️"
        },
        {
          type: "mc",
          q: "Friend: 'But it's only Saturday!' You: 'I know, but I ___ it today because I'm busy tomorrow.'",
          options: [
            "am doing",
            "do",
            "did"
          ],
          a: "am doing",
          tip: "⏰ EXCEPTION TO ROUTINE!\n\nNormal: Do laundry on Sundays (routine)\nToday: Saturday (exception!)\n\nException = happening NOW/today:\n✅ 'I'm doing it today' (present continuous)\n\nWhy continuous?\nIt's not the normal routine!\nIt's a specific plan for TODAY!\n\nContrast:\n'I usually do it on Sunday' (simple)\n'But I'm doing it today' (continuous)\n\nThis shows the difference! 🎯",
          successtip: "✓ EXCEPTION!\n'Am doing' = today's specific action, not the usual routine! Change of plan! 📅"
        },
        {
          type: "mc",
          q: "You: 'Actually, I ___ the laundry right now as we speak!'",
          options: [
            "am doing",
            "do",
            "did"
          ],
          a: "am doing",
          successtip: "✓ THIS VERY MOMENT!\n'Right now' + 'as we speak' = continuous! Action happening during conversation! 🧺📞"
        },
        {
          type: "order",
          q: "Traduce: ¿Haces la cama todos los días? (habit question)",
          a: "Do you make the bed every day?",
          successtip: "✓ ROUTINE QUESTION!\n'Do you make...every day?' = asking about habit. Present simple question! 🛏️❓"
        },
        {
          type: "mc",
          q: "Friend: 'What ___ you usually ___ on Saturday mornings?' (habit)",
          options: [
            "do / do",
            "are / doing",
            "did / do"
          ],
          a: "do / do",
          successtip: "✓ ROUTINE QUESTION!\n'What do you usually do?' = asking about habits. Present simple! 🔄❓"
        },
        {
          type: "blank",
          q: "You: 'I ___ the windows right now - they're really dirty!' (clean + -ing)",
          a: "am cleaning",
          accept: { ignoreCase: true, alts: ["'m cleaning"] },
          successtip: "✓ CURRENT ACTION!\n'Right now' = continuous! Mid-window-cleaning during phone call! 🪟💧📞"
        },
        {
          type: "mc",
          q: "Complete contrast: 'I always ___ my room on Fridays, but today I ___ it early because guests are coming.'",
          options: [
            "tidy / am tidying",
            "tidy / tidy",
            "am tidying / tidy"
          ],
          a: "tidy / am tidying",
          successtip: "✓ PERFECT CONTRAST!\nRoutine (always tidy) vs exception (am tidying today) = tense contrast mastered! ⏰✨"
        }
      ]
    },

    // ========================================================================
    // SET 9: WHAT ARE YOU DOING? 📞 - MORE CONTRAST
    // ========================================================================
    {
      id: "m7-u6-s9",
      title: "Set 9: Current vs Habitual Actions 📞",
      context: `**Unexpected Visitor**

*Doorbell rings*

You: "Just a minute! I'm in the middle of cleaning!"

Visitor: "Oh sorry! Should I come back later?"`,
      
      questions: [
        {
          type: "mc",
          q: "Visitor: 'What ___ you ___ right now?'",
          options: [
            "are / doing",
            "do / do",
            "did / do"
          ],
          a: "are / doing",
          successtip: "✓ CURRENT QUESTION!\n'What are you doing?' = asking about NOW. Most common present continuous question! 📞"
        },
        {
          type: "blank",
          q: "You: 'I ___ the carpet. Come in, it's fine!' (vacuum + -ing)",
          a: "am vacuuming",
          accept: { ignoreCase: true, alts: ["'m vacuuming", "am hoovering", "'m hoovering"] },
          successtip: "✓ MID-ACTION!\n'Am vacuuming' = in progress when doorbell rang! UK: 'hoovering' also works! 🧹🚪"
        },
        {
          type: "mc",
          q: "Visitor: '___ you usually ___ on weekday mornings?' (habit)",
          options: [
            "Do / clean",
            "Are / cleaning",
            "Did / clean"
          ],
          a: "Do / clean",
          successtip: "✓ HABIT QUESTION!\n'Do you usually...?' = asking about routine, not now. Present simple! 🔄"
        },
        {
          type: "order",
          q: "Traduce: No, generalmente trabajo en casa los lunes",
          a: "No, I usually work from home on Mondays",
          successtip: "✓ ROUTINE ANSWER!\n'Usually work' = habit. Answering routine question with simple tense! 💻📅"
        },
        {
          type: "mc",
          q: "You: 'But today I ___ because I have the day off.'",
          options: [
            "am cleaning",
            "clean",
            "cleaned"
          ],
          a: "am cleaning",
          successtip: "✓ TODAY'S EXCEPTION!\n'Today I am cleaning' = not usual routine, just today! Exception = continuous! 🧹📅"
        },
        {
          type: "mc",
          q: "Visitor: 'I ___ my house on Saturdays usually. Today is my lazy day!'",
          options: [
            "clean",
            "am cleaning",
            "cleaned"
          ],
          a: "clean",
          successtip: "✓ THEIR ROUTINE!\n'Usually' + Saturdays = routine = simple! Today is different (lazy day)! 😴📅"
        },
        {
          type: "blank",
          q: "You: 'I ___ the dishes every night after dinner.' (do - habit)",
          a: "do",
          accept: { ignoreCase: true },
          successtip: "✓ NIGHTLY HABIT!\n'Every night' = routine = present simple 'do'! Regular pattern! 🍽️🌙"
        },
        {
          type: "mc",
          q: "Visitor notices: 'Your roommate ___ the laundry right now - I can hear the machine!'",
          options: [
            "is doing",
            "does",
            "did"
          ],
          a: "is doing",
          successtip: "✓ OBSERVATION!\n'Right now' + hearing it = continuous! Happening this moment! 🧺🔊"
        },
        {
          type: "order",
          q: "Traduce: ¿Normalmente haces las tareas domésticas los fines de semana?",
          a: "Do you normally do household chores on weekends?",
          successtip: "✓ WEEKEND ROUTINE?\n'Normally do' = asking about usual pattern. Routine question! 🧹📅❓"
        },
        {
          type: "mc",
          q: "Final contrast: 'I never ___ the bed during the week, but today I ___ it because guests are staying.'",
          options: [
            "make / am making",
            "make / make",
            "am making / make"
          ],
          a: "make / am making",
          successtip: "✓ NEVER vs TODAY!\n'Never make' (routine) vs 'am making' (today's exception) = perfect contrast! 🛏️✨"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE MASTERY 🏡 - ALL INTEGRATED
    // ========================================================================
    {
      id: "m7-u6-s10",
      title: "Set 10: Complete Home & Routine Mastery 🏡",
      context: `**FINAL CHALLENGE**

You're writing a blog post about your daily home life. Use everything: house vocabulary, collocations, UK-US terms, and tense contrast!`,
      
      questions: [
        {
          type: "mc",
          q: "Blog: 'I live in a semi-detached ___ in the UK.' (UK term for apartment)",
          options: [
            "Actually, semi-detached = house, not flat",
            "flat",
            "apartment"
          ],
          a: "Actually, semi-detached = house, not flat",
          tip: "🏠 VOCABULARY CHECK!\n\n'Semi-detached' = type of HOUSE (shares one wall)\n'Flat' = apartment (in a building)\n\nThey're different!\n\nSemi-detached = house type\nFlat = apartment type\n\nCan't mix them!\n\nCorrect: 'I live in a semi-detached house'\nOR: 'I live in a flat'\n\nNot: 'semi-detached flat' ❌🎯",
          successtip: "✓ VOCABULARY MIX-UP CAUGHT!\nSemi-detached = house type! Can't be a flat! Good catch! 🏠✅"
        },
        {
          type: "blank",
          q: "Blog: 'Every morning, I ___ the bed and ___ breakfast.' (two verbs - collocations!)",
          a: "make, make",
          accept: { ignoreCase: true, alts: ["make / make", "make, cook"] },
          successtip: "✓ BOTH 'MAKE'!\nMake the bed + make breakfast = both use 'make'! Key collocations! 🛏️🍳"
        },
        {
          type: "mc",
          q: "Blog: 'I usually ___ the dishes in the evening.' (routine - collocation)",
          options: [
            "do",
            "make",
            "clean"
          ],
          a: "do",
          successtip: "✓ DO DISHES!\n'Do the dishes' = wash them. Routine = simple tense! Evening chore! 🍽️🌙"
        },
        {
          type: "order",
          q: "Traduce: Normalmente hago la colada los domingos, pero esta semana la estoy haciendo hoy",
          a: "I usually do the laundry on Sundays, but this week I am doing it today",
          successtip: "✓ PERFECT CONTRAST!\nUsually do (routine) + am doing (exception) = tense contrast mastered! 🧺⏰"
        },
        {
          type: "mc",
          q: "Blog: 'Right now, I ___ the windows because they're dirty.'",
          options: [
            "am cleaning",
            "clean",
            "cleaned"
          ],
          a: "am cleaning",
          successtip: "✓ CURRENT ACTION!\n'Right now' = continuous! Mid-window-cleaning as you blog! 🪟💧"
        },
        {
          type: "mc",
          q: "Blog: 'My ___ (UK term) has four bedrooms and a large garden.'",
          options: [
            "house",
            "flat",
            "apartment"
          ],
          a: "house",
          successtip: "✓ HOUSE!\n4 bedrooms + large garden = definitely a house, not flat! Houses have gardens (yards)! 🏡🌳"
        },
        {
          type: "blank",
          q: "Blog: 'In the UK, we call the area outside a ___, but Americans call it a yard.'",
          a: "garden",
          accept: { ignoreCase: true },
          successtip: "✓ UK-US DIFFERENCE!\nGarden (UK) = Yard (US). Important vocabulary distinction! 🇬🇧🌳🇺🇸"
        },
        {
          type: "mc",
          q: "Blog: 'I need to ___ the lawn - the grass is getting long!'",
          options: [
            "mow / cut",
            "clean",
            "make"
          ],
          a: "mow / cut",
          successtip: "✓ GARDEN WORK!\n'Mow the lawn' or 'cut the lawn/grass' = use lawn mower! Outdoor chore! 🌱✂️"
        },
        {
          type: "mc",
          q: "Blog: 'After dinner, I always ___ the table and ___ the dishes.'",
          options: [
            "clear / do",
            "clean / make",
            "make / do"
          ],
          a: "clear / do",
          successtip: "✓ TWO COLLOCATIONS!\nClear the table (remove items) + do the dishes (wash) = dinner cleanup routine! 🍽️✨"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: This blog post integrates house types, rooms, furniture, collocations (make/do), UK-US terms, and tense contrast. Which sentence shows COMPLETE mastery?",
          options: [
            "'I live in a detached house with a garden (UK: garden, US: yard), and every morning I make the bed, but right now I'm doing the laundry because I usually do it on Sundays.'",
            "'I live in a detached flat with a yard, and every morning I do the bed, but right now I make the laundry because I usually make it on Sundays.'",
            "'I living in house with garden, and every morning I making the bed, but right now I do the laundry because I doing it on Sundays.'"
          ],
          a: "'I live in a detached house with a garden (UK: garden, US: yard), and every morning I make the bed, but right now I'm doing the laundry because I usually do it on Sundays.'",
          tip: "🎓 COMPLETE UNIT 6 MASTERY!\n\nThis sentence has EVERYTHING:\n\n✅ House vocabulary:\n• Detached house (correct type) ✅\n• Garden (UK term noted) ✅\n\n✅ Collocations:\n• MAKE the bed ✅\n• DO the laundry ✅\n\n✅ Tense contrast:\n• 'I make' (routine - simple) ✅\n• 'I'm doing' (now - continuous) ✅\n• 'I usually do' (routine - simple) ✅\n\n✅ UK-US awareness:\n• Garden (UK) = Yard (US) noted ✅\n\nAll Unit 6 concepts integrated perfectly! 🌟",
          successtip: "🎉 UNIT 6 COMPLETELY MASTERED! 🎉\n\n✅ HOUSE VOCABULARY\n• Types: detached, semi-detached, terraced, flat ✅\n• Rooms: living room, bedroom, kitchen, bathroom, study ✅\n• Outdoor: garden/yard, lawn, shed, driveway ✅\n\n✅ FURNITURE & APPLIANCES\n• Living room: sofa, armchair, coffee table, rug ✅\n• Bedroom: wardrobe/closet, chest of drawers/dresser ✅\n• Kitchen: fridge, cooker/stove, dishwasher, kettle ✅\n• Bathroom: basin/sink, tap/faucet, bathtub ✅\n\n✅ HOUSEHOLD COLLOCATIONS\n• MAKE: the bed, breakfast/dinner ✅\n• DO: the laundry, the dishes, the cleaning ✅\n• CLEAN: the windows, the floor ✅\n• WASH: the dishes, up (UK) ✅\n• TIDY: the room, up ✅\n• VACUUM: the carpet ✅\n• CLEAR: the table ✅\n\n✅ UK-US DIFFERENCES\n• Flat (UK) = Apartment (US) ✅\n• Garden (UK) = Yard (US) ✅\n• Tap (UK) = Faucet (US) ✅\n• Rubbish (UK) = Trash (US) ✅\n• Wardrobe (UK) = Closet (US) ✅\n• Basin (UK) = Sink (US) ✅\n\n✅ TENSE CONTRAST\n• Present Simple (routines): I usually make... ✅\n• Present Continuous (now): I'm making... ✅\n• Contrast: I usually cook, but I'm eating out ✅\n\n🌟 YOU CAN NOW:\n• Describe any house completely ✅\n• Use household collocations correctly ✅\n• Understand UK-US vocabulary differences ✅\n• Contrast routine vs current actions ✅\n• Talk about daily chores naturally ✅\n\nB1-B2 home & routines = MASTERED! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - MOVING INTO A NEW HOUSE (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u6-reading",
    title: "Reading Practice - Moving into a New House",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: House Hunting (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Moving into a New House**

**Part 1: House Hunting**

We're looking for a new home. We visited a lovely detached house today with four bedrooms, a large garden, and a garage. It has a modern kitchen and two bathrooms. The estate agent says it's perfect for families.`
      },
      {
        type: "mc",
        q: "What type of house are they looking at?",
        options: [
          "Detached house",
          "Terraced house",
          "Flat/Apartment"
        ],
        a: "Detached house",
        successtip: "✓ HOUSE TYPE!\n'Detached house' = standalone, not connected to others. Most private option! 🏡"
      },
      {
        type: "blank",
        q: "In the UK, the outdoor area is called a ___.",
        a: "garden",
        accept: { ignoreCase: true },
        successtip: "✓ UK TERM!\n'Garden' mentioned in text. US would say 'yard'! Vocabulary difference! 🇬🇧🌳"
      },

      // ======================================================================
      // BLOQUE 2: First Day - Room Tour (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 2: Moving Day - Room Tour**

We're finally here! The living room is spacious with a large window. The main bedroom has a wardrobe and a bedside table. The kitchen has a dishwasher and a new cooker. We're so excited to start unpacking!`
      },
      {
        type: "mc",
        q: "'Wardrobe' is the UK term. What's the US equivalent?",
        options: [
          "Closet",
          "Dresser",
          "Cabinet"
        ],
        a: "Closet",
        successtip: "✓ US vs UK!\n'Wardrobe' (UK furniture) = 'Closet' (US built-in). Same function, different style! 🇬🇧🇺🇸👔"
      },
      {
        type: "mc",
        q: "The UK term 'cooker' means:",
        options: [
          "Stove (US term)",
          "Microwave",
          "Refrigerator"
        ],
        a: "Stove (US term)",
        successtip: "✓ COOKING APPLIANCE!\n'Cooker' (UK) = 'Stove' (US). For cooking food! Cultural vocabulary! 🇬🇧🍳"
      },

      // ======================================================================
      // BLOQUE 3: Unpacking Furniture (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 3: Unpacking**

Today we're unpacking furniture. We're putting the sofa and coffee table in the living room. The chest of drawers is going in the bedroom. We need a rug for the floor. The bathroom needs a new basin tap.`
      },
      {
        type: "mc",
        q: "What tense is primarily used? 'We're putting', 'is going':",
        options: [
          "Present continuous (actions happening now)",
          "Present simple",
          "Past simple"
        ],
        a: "Present continuous (actions happening now)",
        successtip: "✓ CURRENT ACTIONS!\n'We're putting/unpacking/going' = present continuous! Happening during moving day! 📦⏰"
      },
      {
        type: "blank",
        q: "UK term: 'basin ___' = US term: 'sink faucet'",
        a: "tap",
        accept: { ignoreCase: true },
        successtip: "✓ WATER CONTROL!\n'Tap' (UK) = 'Faucet' (US). Controls water flow! Bathroom vocabulary! 🚰🇬🇧"
      },

      // ======================================================================
      // BLOQUE 4: First Morning - Breakfast Routine (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 4: First Morning**

It's our first morning! I usually make the bed immediately, but today I'm making breakfast first. I'm boiling water in the kettle for tea. Later, I need to do the laundry and clean the windows. Busy day ahead!`
      },
      {
        type: "mc",
        q: "Identify the tense contrast: 'I usually make' vs 'I'm making':",
        options: [
          "Simple (routine) vs Continuous (today's exception)",
          "Both are routines",
          "Both are current actions"
        ],
        a: "Simple (routine) vs Continuous (today's exception)",
        successtip: "✓ TENSE CONTRAST!\n'Usually make' (routine) vs 'I'm making' (today) = perfect contrast! Unit 6 grammar! ⏰✨"
      },
      {
        type: "mc",
        q: "Which collocations are used correctly?",
        options: [
          "Make the bed, do the laundry",
          "Do the bed, make the laundry",
          "Clean the bed, wash the laundry"
        ],
        a: "Make the bed, do the laundry",
        successtip: "✓ COLLOCATIONS!\nMAKE the bed + DO the laundry = correct fixed expressions! Never switch! 🛏️🧺✅"
      },

      // ======================================================================
      // BLOQUE 5: Weekend - Household Chores + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 5: First Weekend**

Weekend chores! I'm vacuuming the carpets and my partner is cleaning the bathroom. We usually do household chores on Saturdays. Later, we need to mow the lawn and tidy the garden. Our new house is wonderful but needs work!`
      },
      {
        type: "mc",
        q: "How many household collocations can you find?",
        options: [
          "Five: vacuum carpets, clean bathroom, do chores, mow lawn, tidy garden",
          "Only two",
          "None - they're all regular verbs"
        ],
        a: "Five: vacuum carpets, clean bathroom, do chores, mow lawn, tidy garden",
        successtip: "✓ COLLOCATION COUNT!\nAll five are correct verb-noun pairings! Unit 6 vocabulary in action! 🧹✨"
      },
      {
        type: "mc",
        q: "'Mow the lawn' means:",
        options: [
          "Cut the grass",
          "Water the plants",
          "Plant flowers"
        ],
        a: "Cut the grass",
        successtip: "✓ GARDEN WORK!\n'Mow' = cut with lawn mower. Essential garden maintenance! 🌱✂️"
      },
      {
        type: "mc",
        q: "Throughout this reading, which UK vocabulary terms appeared?",
        options: [
          "All of these: garden, wardrobe, cooker, basin, tap, kettle",
          "None - all were US terms",
          "Only 'garden'"
        ],
        a: "All of these: garden, wardrobe, cooker, basin, tap, kettle",
        successtip: "✓ UK VOCABULARY!\nAll six UK terms used! US equivalents: yard, closet, stove, sink, faucet, (no exact US word for kettle - less common)! 🇬🇧"
      },
      {
        type: "mc",
        q: "FINAL QUESTION: This reading integrated house types, rooms, furniture, UK-US terms, collocations, and tense contrast. What did you notice?",
        options: [
          "All Unit 6 concepts appeared naturally in a moving story",
          "Only vocabulary was used",
          "No grammar patterns"
        ],
        a: "All Unit 6 concepts appeared naturally in a moving story",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ HOUSE VOCABULARY\n• Detached house, rooms, garden ✅\n\n✅ FURNITURE\n• Sofa, wardrobe, chest of drawers, bedside table ✅\n\n✅ APPLIANCES\n• Dishwasher, cooker, kettle ✅\n\n✅ UK-US TERMS\n• Garden/yard, wardrobe/closet, cooker/stove, basin/sink, tap/faucet ✅\n\n✅ COLLOCATIONS\n• Make the bed, do the laundry, clean bathroom, vacuum carpets, mow lawn ✅\n\n✅ TENSE CONTRAST\n• 'Usually make' (routine) vs 'I'm making' (now) ✅\n• 'We usually do' (habit) vs 'I'm vacuuming' (current) ✅\n\n🌟 ALL UNIT 6 IN ACTION!\nVocabulary + Collocations + Grammar = Natural English! 💪📖"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 6 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u6-review",
    title: "⭐ Unit 6 Review: Home & Daily Routines",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which collocation is CORRECT?",
        options: [
          "Make the bed",
          "Do the bed",
          "Fix the bed"
        ],
        a: "Make the bed",
        tip: "🎯 REVIEW: COLLOCATIONS!\n\nFixed expressions:\n✅ MAKE the bed (create orderly bed)\n❌ DO the bed (WRONG!)\n❌ FIX the bed (different meaning - repair)\n\nCollocations are FIXED!\nYou MUST use the correct verb!\n\nMAKE: bed, breakfast, dinner\nDO: laundry, dishes, cleaning\n\nThis is Unit 6's core grammar! 🎯",
        successtip: "✓ MAKE THE BED!\nBonus: Morning routine essential! 'Making the bed' = first chore! 🛏️✨"
      },
      
      {
        type: "blank",
        q: "UK term for apartment: ___",
        a: "flat",
        accept: { ignoreCase: true },
        successtip: "✓ BRITISH ENGLISH!\n'Flat' (UK) = 'Apartment' (US). Same thing, different word! 🇬🇧🏢"
      },
      
      {
        type: "mc",
        q: "'I usually ___ the dishes after dinner, but tonight I ___ them now.'",
        options: [
          "do / am doing",
          "do / do",
          "am doing / do"
        ],
        a: "do / am doing",
        successtip: "✓ TENSE CONTRAST!\nRoutine (usually do) vs exception (am doing now) = perfect! Also: correct collocation! 🍽️⏰"
      },
      
      {
        type: "order",
        q: "Traduce: El jardín necesita ser cortado - el césped está muy largo",
        a: "The garden needs to be cut - the lawn is very long",
        successtip: "✓ GARDEN vs LAWN!\n'Garden' (UK) = whole outdoor space. 'Lawn' = just the grass part! UK vocabulary! 🇬🇧🌳"
      },
      
      {
        type: "mc",
        q: "Which is the INCORRECT collocation?",
        options: [
          "Make the laundry",
          "Do the laundry",
          "Make the bed"
        ],
        a: "Make the laundry",
        successtip: "✓ ERROR CAUGHT!\n'Do the laundry' = correct! 'Make the laundry' = WRONG! Never confuse make/do! 🧺✅❌"
      },
            {
        type: "blank",
        q: "US: 'trash' = UK: '___'",
        a: "rubbish",
        accept: { ignoreCase: true },
        successtip: "✓ GARBAGE TALK!\n'Rubbish' (UK) = 'Trash/Garbage' (US). 'Take out the rubbish/trash'! 🗑️🇬🇧🇺🇸"
      },
      
      {
        type: "mc",
        q: "After eating, you need to ___ the table.",
        options: [
          "clear",
          "clean",
          "empty"
        ],
        a: "clear",
        successtip: "✓ TABLE COLLOCATION!\n'Clear the table' = remove dishes after eating. Then you 'clean' it (wipe)! Two steps! 🍽️"
      },
      
      {
        type: "order",
        q: "Traduce: ¿Normalmente haces tu cama todos los días?",
        a: "Do you normally make your bed every day?",
        successtip: "✓ ROUTINE QUESTION!\n'Normally' + 'every day' = habit question = present simple! Plus correct collocation! 🛏️❓"
      },
      
      {
        type: "mc",
        q: "UK: 'wardrobe' = US: '___'",
        options: [
          "closet",
          "dresser",
          "cupboard"
        ],
        a: "closet",
        successtip: "✓ CLOTHES STORAGE!\nUK: 'wardrobe' (furniture). US: 'closet' (built-in). Both for hanging clothes! 👔🇬🇧🇺🇸"
      },
      
      {
        type: "mc",
        q: "FINAL MASTERY: Which sentence demonstrates COMPLETE Unit 6 mastery?",
        options: [
          "'I live in a detached house with a garden (UK) / yard (US), and every morning I make the bed and do the laundry, but today I'm cleaning the windows because I usually clean them on Saturdays.'",
          "'I live in a detached flat with a yard, and every morning I do the bed and make the laundry, but today I clean the windows because I cleaning them on Saturdays.'",
          "'I living in house with garden, every morning I making bed and doing laundry, today I cleaning windows because I usually cleaning on Saturdays.'"
        ],
        a: "'I live in a detached house with a garden (UK) / yard (US), and every morning I make the bed and do the laundry, but today I'm cleaning the windows because I usually clean them on Saturdays.'",
        tip: "🎓 COMPLETE UNIT 6 INTEGRATION!\n\nThis sentence has EVERYTHING:\n\n✅ House vocabulary:\n• Detached house (correct type) ✅\n• Garden (UK) vs Yard (US) noted ✅\n\n✅ Collocations (all correct):\n• MAKE the bed (not 'do') ✅\n• DO the laundry (not 'make') ✅\n• CLEAN the windows ✅\n\n✅ Tense contrast:\n• 'I make' (routine - simple) ✅\n• 'I do' (routine - simple) ✅\n• 'I'm cleaning' (today - continuous) ✅\n• 'I usually clean' (habit - simple) ✅\n\n✅ UK-US awareness:\n• Garden/yard distinction shown ✅\n\nAll Unit 6 concepts perfectly integrated! 🌟",
        successtip: "🎉 UNIT 6 COMPLETELY MASTERED! 🎉\n\n✅ HOUSE VOCABULARY\n• Types: detached, semi-detached, terraced, flat/apartment ✅\n• Rooms: living room, bedroom, kitchen, bathroom, study, utility room ✅\n• Outdoor: garden/yard, lawn, garage, driveway, patio, shed ✅\n\n✅ FURNITURE BY ROOM\n• Living: sofa/couch, armchair, coffee table, rug, bookshelf ✅\n• Bedroom: wardrobe/closet, chest of drawers/dresser, bedside table/nightstand ✅\n• Kitchen: fridge, cooker/stove, dishwasher, kettle, saucepan ✅\n• Bathroom: basin/sink, bathtub, tap/faucet, shower ✅\n\n✅ HOUSEHOLD COLLOCATIONS\n• MAKE: the bed, breakfast/lunch/dinner, a mess ✅\n• DO: the laundry, the dishes, the cleaning, the ironing ✅\n• CLEAN: the windows, the floor, the bathroom ✅\n• WASH: the dishes, the car, up (UK) ✅\n• TIDY: the room, up ✅\n• VACUUM: the carpet/floor ✅\n• CLEAR: the table ✅\n• MOW: the lawn ✅\n• TAKE OUT: the rubbish/trash ✅\n\n✅ UK-US DIFFERENCES\n• Flat (UK) = Apartment (US) ✅\n• Garden (UK) = Yard (US) ✅\n• Sitting room (UK) = Living room (US) ✅\n• Tap (UK) = Faucet (US) ✅\n• Rubbish (UK) = Trash/Garbage (US) ✅\n• Wardrobe (UK) = Closet (US) ✅\n• Cooker (UK) = Stove (US) ✅\n• Basin (UK) = Sink (US) ✅\n• Chest of drawers (UK) = Dresser (US) ✅\n• Pavement (UK) = Sidewalk (US) ✅\n\n✅ TENSE CONTRAST\n• Present Simple = routines, habits ✅\n  'I usually make the bed'\n  'She does the laundry on Sundays'\n• Present Continuous = actions NOW ✅\n  'I'm making the bed right now'\n  'She's doing the laundry at the moment'\n• Contrast in sentences ✅\n  'I usually cook, but I'm eating out tonight'\n\n✅ REAL-WORLD CONTEXTS\n• House hunting ✅\n• Moving in ✅\n• Daily chores ✅\n• Weekend cleaning ✅\n• Preparing for guests ✅\n\n🌟 YOU CAN NOW:\n• Describe any home completely ✅\n• Use household collocations perfectly ✅\n• Understand UK-US vocabulary differences ✅\n• Contrast routine vs current actions ✅\n• Talk about daily household tasks naturally ✅\n• Navigate both British and American English ✅\n\nB1-B2 home & daily routines = MASTERED! 💪\n\n¡Excelente trabajo! Ready for Unit 7! 🚀"
      }
    ]
  }
};
